import { formatMonthName } from '@budgie/shared';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { invalidateFinancials } from '../../lib/invalidate';
import { type RouterOutputs, trpc } from '../../lib/trpc';
import { DECIMAL_INPUT_PATTERN, oreToInputValue, parseMoneyInput } from '../settings/moneyInput';

type FixedCostItem = RouterOutputs['period']['summary']['fixedCosts']['items'][number];

/** Sets what an unpaid bill or loan is expected to cost in one period. */
export function FixedCostDialog({
  item,
  year,
  month,
  onClose,
}: {
  item: FixedCostItem;
  year: number;
  month: number;
  onClose: () => void;
}) {
  const [amount, setAmount] = useState(oreToInputValue(item.expected));
  const [formError, setFormError] = useState<string | null>(null);
  const utils = trpc.useUtils();

  const onSuccess = () => {
    void invalidateFinancials(utils);
    toast.success(`${item.name} uppdaterad`);
    onClose();
  };
  const onError = (err: { message: string }) => setFormError(err.message);
  const billMutation = trpc.budget.updateBillEntry.useMutation({ onSuccess, onError });
  const loanMutation = trpc.budget.updateLoanEntry.useMutation({ onSuccess, onError });
  const pending = billMutation.isPending || loanMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const expectedAmount = parseMoneyInput(amount);
    if (expectedAmount === null) {
      setFormError('Ange ett giltigt belopp, t.ex. 1 250.');
      return;
    }
    setFormError(null);
    if (item.kind === 'bill') {
      billMutation.mutate({ recurringBillId: item.id, year, month, expectedAmount });
    } else {
      loanMutation.mutate({ loanId: item.id, year, month, expectedAmount });
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{item.name}</DialogTitle>
          <DialogDescription>
            Hur mycket väntar ni att {item.kind === 'loan' ? 'lånet' : 'räkningen'} kostar i{' '}
            {formatMonthName(month).toLowerCase()}? Gäller bara den här perioden.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="fixed-cost-amount" className="text-xs text-muted-foreground">
              Belopp (kr)
            </Label>
            <Input
              id="fixed-cost-amount"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              pattern={DECIMAL_INPUT_PATTERN}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 tabular-nums"
              autoFocus
            />
          </div>

          {formError && (
            <p role="alert" className="text-sm text-negative">
              {formError}
            </p>
          )}

          <DialogFooter className="max-sm:grid max-sm:grid-cols-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Avbryt
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? 'Sparar…' : 'Spara'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
